// The vendor's own area on aldoo.com.
//
// A vendor is a member of an organization, not a platform admin, and they
// work here rather than in /admin: their orders, their sales, their products,
// their company. The panels are the very ones the platform admin's vendors
// page shows (AdminVendors/panel.jsx and the Orders / Sales tabs) — what
// differs is the shell around them: the organization is the subject of the
// page, so it names the panel, and the sections are a menu down the side
// rather than tabs across the top.
import React, { useEffect, useState } from "react"
import {
  ArrowLeftOnRectangleIcon,
  BanknotesIcon, ArrowDownTrayIcon, StarIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  Squares2X2Icon,
  TruckIcon,
  CubeIcon,
  ShoppingBagIcon,
  UserGroupIcon,
  IdentificationIcon, CreditCardIcon } from "@heroicons/react/24/outline"
import { CheckBadgeIcon } from "@heroicons/react/24/solid"
import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline"
import { PlusCircleIcon, ArrowLeftIcon, ArrowRightIcon, BuildingOffice2Icon } from "@heroicons/react/24/outline"
import { twMerge } from "tailwind-merge"
import Tabs from "aldoo-ra/Tabs"
import Value from "aldoo-ra/Value"
import tabsVariants from "../Variants/Tabs"
import { usePageContext } from "vike-react/usePageContext"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import usePortalTexts from "./texts/usePortalTexts"
import { PORTAL as PORTAL_TEXTS } from "./texts/bundles"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import StoreProducts from "aldoo-ra/Admin/Store/Products"
import useSubmissionEvents from "./useSubmissionEvents"
import useVendorProductColumns, { ReviewDot, AttentionChip, useAttention } from "./useVendorProductColumns"
import useVendorProductFilters from "./useVendorProductFilters"
import useVendorProductLabels from "./useVendorProductLabels"
import { orgLogoUrl } from "aldoo-ra/Organizations/api"
import { useUrlParam, writeUrl } from "aldoo-ra/URLState"

const PORTAL = /^\/vendor(\/|$)/
import { setEndpointAlias } from "aldoo-ra/API"
import OrdersTab from "../AdminVendors/OrdersTab"
import SalesTab from "../AdminVendors/SalesTab"
import { keepSalesRef } from "./salesRef"
import CreateProductDialog from "../AdminVendors/CreateProductDialog"
import { MembersPanel, ProfileEditor, SettingsEditor, ShippingEditor, PaymentMethodsEditor } from "../AdminVendors/panel"
import { GetVendorProfile, ListVendorOrgs, ListOrders } from "../AdminShared/api"
import useStoreEvents from "aldoo-ra/Store/useStoreEvents"
import { Empty, Thumb, errorMessage } from "../AdminShared/ui"
import useClientAuth from "../Storefront/hooks/useClientAuth"
import clientRedirect from "../Storefront/utils/clientRedirect"
import useSiteLanguage from "../Storefront/hooks/useSiteLanguage"
import { sitePath } from "../Storefront/SiteLinks"
import Spinner from "../Storefront/ui/Spinner"
import { stripLang } from "../Storefront/utils/contentRoutes"
import OrgPicker from "./OrgPicker"
import PayoutsPanel from "../Payouts/PayoutsPanel"
import ImportTab from "./ImportTab"
import Registration from "./Registration"
import SendForReview from "./SendForReview"
import MerchantLanding from "./MerchantLanding"
import { forgetVendorOrgs } from "./useVendorOrgs"
import StoreDashboards from "aldoo-ra/StoreDashboards"
import EligibilityChecklist, { highlightField, eligibilityOf } from "./EligibilityChecklist"
import ContractRequiredDialog from "./ContractRequiredDialog"
import OnboardingGuide, { onboardingSteps } from "./OnboardingGuide"
import CatalogHoldNotice from "./CatalogHoldNotice"
import ContractPendingNotice from "./ContractPendingNotice"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"
import FeedbackTable from "../AdminShared/feedback/FeedbackTable"
import { ALDOO_ADVISOR } from "../AdminShared/advisor-brand"
import { rememberedOrg, rememberOrg } from "./currentOrg"
import { grantsPermission } from "aldoo-ra/Organizations/api"

/**
 * The sections of the area. "Company" is what a vendor calls their own
 * profile — the account's own profile is a different thing, and it stays
 * under My account.
 *
 * `permission`: the company's permission that opens the section (the
 * organization's roles, aldoo-com-api vendors/lib/org-permissions): a
 * member sees only the sections they may read, and changes what they may
 * write (`write`, asked with can()). An admin member and a platform admin
 * hold every one.
 */
const SECTIONS = [
  { id: "orders", label: "Orders", icon: ShoppingBagIcon, verified: true, contract: true, permission: "vendor-orders" },
  { id: "sales", label: "Sales", icon: ChartBarIcon, verified: true, contract: true, permission: "vendor-sales" },
  { id: "products", label: "Products", icon: CubeIcon, verified: true, contract: true, permission: "vendor-products" },
  // the company's own dashboards: the cards each member composes
  { id: "dashboard", label: "Dashboard", icon: Squares2X2Icon, verified: true, contract: true, permission: "vendor-dashboard" },
  { id: "company", label: "Company", icon: BuildingStorefrontIcon, permission: "vendor-company" },
  // where a company applies, and where it waits: gone once it is verified,
  // because there is nothing left to say
  { id: "registration", label: "Registration", icon: IdentificationIcon, untilVerified: true, permission: "vendor-company" },
  { id: "settings", label: "Settings", icon: Cog6ToothIcon, verified: true, permission: "vendor-settings" },
  // the couriers the company ships with, and the ways each one delivers
  { id: "shipping", label: "Delivery", icon: TruckIcon, verified: true, permission: "vendor-shipping" },
  // how buyers pay: online, cash on delivery
  { id: "payment-methods", label: "Payment methods", icon: CreditCardIcon, verified: true, permission: "vendor-payment-methods" },
  // open from the start: a company still applying or setting up brings its team in too
  { id: "members", label: "Members", icon: UserGroupIcon, early: true, permission: "vendor-members" },
  // what is due and when, what Aldoo has paid (the contract and the payout details are in Company)
  { id: "payouts", label: "Payouts", icon: BanknotesIcon, contract: true, permission: "vendor-payouts" },
  // the company's own catalog, imported and kept in step (when Aldoo assigned an importer)
  { id: "import", label: "Import", icon: ArrowDownTrayIcon, verified: true, contract: true, permission: "vendor-import" },
  // what buyers say about the company's products: stars, written reviews, return reasons
  { id: "feedback", label: "Ratings & Comments", icon: StarIcon, verified: true, contract: true, permission: "vendor-feedback" },
]

/**
 * How many of the company's orders are paid and waiting to be sent — the
 * Orders tile "Open" — for the menu to show. Counted again whenever the store
 * announces an order placed, moved on or refunded for this company (/rt/store).
 */
function useOpenOrders(orgId, enabled) {
  const [open, setOpen] = useState(0)
  const [tick, setTick] = useState(0)
  useStoreEvents({ orgId: orgId || null, enabled: !!orgId && enabled, onEvent: () => setTick((n) => n + 1) })
  useEffect(() => {
    if (!orgId || !enabled) return setOpen(0)
    let alive = true
    ListOrders({ company: orgId, page: 1, items_per_page: 1, orderStatus: "paid", withCounts: true })
      .then((res) => alive && setOpen(res?.counts?.paid ?? 0))
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [orgId, enabled, tick])
  return open
}

// the product dialog's tabs in the sellers' help center: the product's details (product-details) and
// what sells it (product-sales-setup) — a tab's id is the heading's id in its article
const PRODUCT_SETUP_TABS = new Set(["inventory", "deliverables", "license-keys", "categories", "tags", "store-page", "discounts", "shipping", "admin-review"])
const productHelpKeyOf = (tab) => (PRODUCT_SETUP_TABS.has(tab) ? `vendor.product.setup#${tab}` : `vendor.product#${tab}`)

const MenuItem = ({ active, icon: Icon, children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    aria-current={active ? "page" : undefined}
    data-testid={testId}
    className={twMerge(
      "w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] text-left transition-colors",
      active ? "bg-admin_primary/10 text-admin_primary font-semibold" : "text-admin_text hover:bg-admin_neutral_1/70"
    )}
  >
    <Icon className="w-5 h-5 shrink-0" />
    <span className="min-w-0 truncate">{children}</span>
  </button>
)

/**
 * Under Company: one more company is one more application. The Aldoo look —
 * the dark card, the orange and the Aldoo mark, as the merchant landing has it.
 */
// hidden for now (too loud under the company's details); true shows it again
const SHOW_ANOTHER_COMPANY = false

const AnotherCompanyBanner = ({ t, onApply }) => (
  <section className="relative overflow-hidden rounded-3xl bg-dark px-6 md:px-10 py-8 md:py-10" data-testid="another-company-banner">
    <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-primary/30 blur-3xl" aria-hidden="true" />
    <div className="absolute -bottom-28 left-1/3 w-72 h-72 rounded-full bg-[#fcda7d]/15 blur-3xl" aria-hidden="true" />
    <div className="relative flex flex-col lg:flex-row lg:items-center gap-6">
      <span className="w-14 h-14 shrink-0 rounded-2xl bg-paper/10 flex items-center justify-center">
        <BuildingOffice2Icon className="w-7 h-7 text-primary" />
      </span>
      <div className="flex-1 min-w-0">
        <span className="inline-flex items-center gap-2 mb-2 text-xs font-bold uppercase tracking-[0.14em] text-primary">
          <img src={ALDOO_ADVISOR.icon} alt="" className="w-4 h-4 object-contain" /> 
          {t`Another company`}
        </span>
        <Typography 
          variant="h4" 
          className="text-text_inv leading-tight mb-2"
        >
          {t`Having more than one company?`}
        </Typography>
        <Typography 
          variant="txt_dim" 
          className="leading-relaxed text-text_inv/75"
        >
          {t`Send a registration request for each of them here. Every company has its own shop, products, orders and payouts.`}
        </Typography>
      </div>
      <Button 
        variant="primary_l" 
        onClick={onApply} 
        className="shrink-0" 
        data-testid="another-company-apply"
      >
        {t`Register another company`} 
        <ArrowRightIcon className="w-5 h-5" />
      </Button>
    </div>
  </section>
)

/**
 * The portal's sections handed to the ☰ menu (Navbar/MobileMenu): on a phone they are the menu, in
 * place of the store's categories. Taken back when the portal closes.
 */
function PortalMenu({ tabs, current, onSelect, onSignOut, onSwitch, org }) {
  const [, setPortalMenu] = Value("vendor-portal-menu")
  // the organization heads the menu: its logo and name
  const company = { name: org?.name || "", logo: org?.logo ? orgLogoUrl(org._id, org.logoUpdatedAt) : null }
  const key = `${current}|${company.name}|${company.logo || ""}|${tabs.map((tab) => `${tab.value}:${tab.label}:${tab.badge || ""}`).join(",")}`
  useEffect(() => {
    setPortalMenu({ items: tabs.map(({ value, label, icon, badge, badgeClassName }) => ({ id: value, label, icon, badge, badgeClassName })), current, select: onSelect, signOut: onSignOut, switchOrg: onSwitch || null, org: company })
  }, [key, !!onSwitch])
  useEffect(() => () => setPortalMenu(null), [])
  return null
}

export default function VendorArea() {
  // a sales agent's sign-up link (?ref=…&lead=…): kept for the application (./salesRef)
  useEffect(() => keepSalesRef(), [])
  const pageContext = usePageContext()
  const { auth } = useClientAuth()
  const logout = PlatformAuth.useLogout()
  const t = usePortalTexts(PORTAL_TEXTS)
  // the site's language: the help center opens in it (its address carries the /bg prefix)
  const language = useSiteLanguage()
  // the portal's params stay on the portal: a link to the site must not
  // carry the organization along
  const [orgId, setOrgId] = useUrlParam("org", { default: "", within: PORTAL })
  const [section, setSection] = useUrlParam("section", { default: "orders", within: PORTAL })
  // a section's own sub-tab in the address belongs to it — Company's (?company=contract), Delivery's (?delivery=…),
  // Settings' (?settings=…): on any other section it is taken out, so it does not ride along
  const [companyTab, setCompanyTab] = useUrlParam("company", { default: "", within: PORTAL })
  const [deliveryTab, setDeliveryTab] = useUrlParam("delivery", { default: "", within: PORTAL })
  const [settingsTab, setSettingsTab] = useUrlParam("settings", { default: "", within: PORTAL })
  const [teamTab, setTeamTab] = useUrlParam("team", { default: "", within: PORTAL })
  // a section picked from the menu: the other sections' sub-tabs leave the address
  const openSection = (id) => {
    setSection(id)
    if (id !== "company" && companyTab) setCompanyTab("")
    if (id !== "shipping" && deliveryTab) setDeliveryTab("")
    if (id !== "settings" && settingsTab) setSettingsTab("")
    if (id !== "members" && teamTab) setTeamTab("")
  }
  const [, setPid] = useUrlParam("pid", { default: "", within: PORTAL })
  // "new": the application form for one more company, for someone who has one
  const [applying, setApplying] = useUrlParam("apply", { default: "", within: PORTAL })
  // "choose": arrived from the header's Sell in Aldoo — someone with several organizations picks one
  // instead of landing in the one they worked in last
  const [choosing, setChoosing] = useUrlParam("choose", { default: "", within: PORTAL })
  const [orgs, setOrgs] = useState(null)
  const [profile, setProfile] = useState(null)
  const [creatingProduct, setCreatingProduct] = useState(false)
  // a new contract waiting for the company's signature stops new products and reviews (ContractRequiredDialog)
  const [contractStop, setContractStop] = useState(false)
  const contractPending = () => !!profile?.payouts?.contractPending && profile?.myRole !== "platform-admin"
  // what the caller may do in this company (profile.myPermissions, "*" for an admin): a section's
  // `read` opens it, its `write` lets them change it; nothing is known until the profile is here
  const can = (resource, action = "read") => !!profile && !profile.error && grantsPermission(profile.myPermissions || [], `${resource}:${action}`)
  // products are added and sent for review once the payout contract is signed; a platform admin looking in may
  // do it meanwhile (to help the company set up)
  const mayAddProducts = (!!profile?.payouts?.contractSigned || profile?.myRole === "platform-admin") && can("vendor-products", "write")
  const newProduct = () => (contractPending() ? setContractStop(true) : setCreatingProduct(true))
  const vendorColumns = useVendorProductColumns()
  const vendorFilters = useVendorProductFilters(orgId)
  const productLabels = useVendorProductLabels()
  // a review decided, sent or withdrawn elsewhere reloads the product list
  const [productsVersion, setProductsVersion] = useState(0)
  // its products that wait for it (Review Required, changes sent back): the list marks them
  const attention = useAttention(orgId, productsVersion)
  useSubmissionEvents({
    orgId: orgId || null,
    enabled: !!orgId,
    events: ["submissions:product-review", "submissions:decision", "submissions:products-visibility", "submissions:contract", "submissions:product-availability"],
    onEvent: (notice) => {
      // a product switched on or off in the store (the visibility matrix's list, another member): the list follows
      if (notice.event === "submissions:product-availability") return setProductsVersion((version) => version + 1)
      // a contract sent or signed: the checklist (Signed payout contract) and the guide follow
      if (notice.event === "submissions:contract") return loadProfile({ quiet: true })
      // Aldoo put the catalog on hold or lifted it (or another member flipped
      // the switch): the settings and the header follow at once
      if (notice.event === "submissions:products-visibility") {
        if (notice.org && String(notice.orgId) === String(orgId)) setProfile((current) => (current?.org ? { ...current, org: { ...current.org, ...notice.org } } : current))
        return
      }
      // the application decided: the menu, the badge and the guide's first step follow
      if (notice.event === "submissions:decision" && notice.kind !== "product") return loadProfile({ quiet: true })
      setProductsVersion((version) => version + 1)
      // a product sent, published or sent back: the guide's last step follows
      loadProfile({ quiet: true })
    },
  })
  // The product dialog is the admin store's, and it reads and writes through
  // the CRUD api — which is the admin's alone. Here its requests go to the
  // portal's own endpoint, scoped to the merchant's organizations. Set during
  // the first render: the dialog's children fetch before this component's
  // effects would run. In the browser only: on the server it would outlive the
  // render (no effect runs there to take it back) and reach other pages.
  useState(() => typeof window !== "undefined" && setEndpointAlias("crud", "vendor-store"))
  // and again whenever it mounts (a remount, StrictMode's included, runs the
  // cleanup first), taken back when the portal goes
  useEffect(() => {
    setEndpointAlias("crud", "vendor-store")
    return () => setEndpointAlias("crud", null)
  }, [])
  // The product editor is built from the CMS models (StoreProduct,
  // StorePrice), which the framework registers only on /admin pages — this
  // area is a storefront page, so it registers them itself.
  const [modelsReady, setModelsReady] = useState(false)
  useEffect(() => {
    let alive = true
    import("aldoo-ra/CMSModels").then((module) => {
      module.default()
      if (alive) setModelsReady(true)
    })
    return () => {
      alive = false
    }
  }, [])

  /**
   * A guest has nothing to be a vendor of — but "not signed in yet" looks
   * exactly like "guest" for the moment before the credentials are read, and
   * bouncing to sign-in in that moment threw away the organization in the
   * url on the way back. So the browser's own storage is asked first, and
   * whatever the url carries is what the sign-in returns to.
   */
  // Nothing to redirect: a guest who follows a merchant portal link is being
  // sold to, not turned away (MerchantLanding below).

  useEffect(() => {
    if (!auth?.auth_token) return
    ListVendorOrgs().then((res) => setOrgs(res?.orgs || []))
  }, [auth?.auth_token])

  /**
   * Whose shops these are. A member is offered the organizations they belong
   * to; a platform admin belongs to none in particular and is offered all of
   * them (`vendor {orgs}` marks those with the role `platform-admin`), which
   * is how they open a vendor's area to help.
   */
  const mine = (orgs || []).filter((o) => o.role && o.role !== "platform-admin")
  const choices = mine.length ? mine : orgs || []

  // one organization needs no choosing, and neither does the one this person
  // was working in last — as long as they still belong to it
  useEffect(() => {
    if (!orgs) return
    // a link naming an organization this person is not in (a forwarded
    // order mail): the choice is theirs, and the rest of the url — the
    // section, the order to open — waits for it
    if (orgId && choices.length && !choices.some((o) => o._id === orgId)) return setOrgId("")
    if (orgId) return
    if (choices.length === 1) return setOrgId(choices[0]._id)
    if (choosing) return
    // the account is read from the browser a moment after the page opens;
    // asking which organization before it is known would ask everybody
    if (!auth?._id) return
    const last = rememberedOrg(auth._id)
    if (last && choices.some((o) => o._id === last)) setOrgId(last)
  }, [orgs, orgId, auth?._id, choosing])

  useEffect(() => {
    if (orgId && auth?._id) rememberOrg(auth._id, orgId)
  }, [orgId, auth?._id])

  const loadProfile = ({ quiet = false } = {}) => {
    if (!orgId) return
    if (!quiet) setProfile(null)
    GetVendorProfile(orgId)
      .then((res) => {
        // a quiet refresh that fails keeps what is on screen
        if (errorMessage(res)) return !quiet && setProfile({ error: errorMessage(res) })
        setProfile(res)
      })
      .catch(() => {})
  }

  /** An organization this person cannot open — a wrong id, or one they left. */
  const unknownOrg = !!orgId && (!!profile?.error || (orgs && !orgs.some((o) => o._id === orgId) && !mine.length))
  // an organization is opened only once it is known to be one of this person's
  // (their list has come): a link to another one opens nothing behind the picker
  const allowed = !!orgId && !!orgs && choices.some((o) => o._id === orgId)
  useEffect(() => {
    if (allowed) return loadProfile()
    setProfile(null)
  }, [orgId, allowed])

  /**
   * A saved organization comes back normalized; it replaces the one in hand at once, then the profile
   * is asked for again, quietly, for the server's checklist (profile.eligibility): the sections' red
   * "!", the guide and the checklist follow what was just filled in.
   */
  const orgSaved = (org) => {
    if (org) setProfile((current) => (current?.org ? { ...current, org: { ...current.org, ...org } } : current))
    loadProfile({ quiet: true })
    ListVendorOrgs().then((res) => setOrgs(res?.orgs || []))
  }

  // "Switch organization" opens the picker over the company in hand; closing it goes back there
  const [switchingFrom, setSwitchingFrom] = useState("")
  const switchOrg = () => {
    setSwitchingFrom(orgId)
    rememberOrg(null, "")
    setOrgId("")
  }
  useEffect(() => {
    if (orgId) setSwitchingFrom("")
  }, [orgId])

  // the Orders item's count (before any early return: it is a hook)
  const openOrders = useOpenOrders(profile?.org?._id, !!profile?.org && !["pending", "rejected"].includes(profile.org.verification) && can("vendor-orders"))

  // not signed in: the shop window, with the two ways in
  if (auth === null) return <MerchantLanding />
  if (!auth?.auth_token || orgs === null) return <Spinner className="py-24" />

  // A link to an organization that is not there, or not theirs: say so, and
  // offer the way back to whatever they do have.
  if (unknownOrg) {
    return (
      <div className="w-full max-w-[560px] mx-auto px-4 py-20 md:py-28 text-center" data-testid="org-not-found">
        <BuildingStorefrontIcon className="w-14 h-14 mx-auto mb-4 text-admin_text_secondary" />
        <Typography 
          component="h1"
          variant="admin_h3"
          className="mb-2"
        >
          {t`Organization not found`}
        </Typography>
        <Typography
          variant="admin_body1_dim" 
          className="mt-0 mb-8"
        >
          {t`This shop does not exist, or your account cannot open it.`}
        </Typography>
        <Button
          variant="admin_primary"
          onClick={() => {
            rememberOrg(null, "")
            setOrgId("")
            setProfile(null)
          }}
          data-testid="org-not-found-home"
        >
          {choices.length ? t`Your organizations` : t`Vendor home`}
        </Button>
      </div>
    )
  }

  // Signed in, selling nothing yet — or asking for one more company (the
  // banner under Company): this is where a company asks to.
  const anotherCompany = applying === "new" && choices.length > 0
  if (!choices.length || anotherCompany) {
    return (
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6 py-10 md:py-16" data-testid="vendor-apply-page">
        {anotherCompany && (
          <button 
            type="button" 
            onClick={() => setApplying("")} 
            className="mb-6 flex w-fit items-center gap-2 text-[14px] font-semibold text-text_secondary hover:text-primary" 
            data-testid="apply-back"
          >
            <ArrowLeftIcon className="w-4 h-4" /> 
            {t`Back to your company`}
          </button>
        )}

        {/* the merchant landing's header: an eyebrow, a large title, the promise */}
        <span className="block mb-3 text-[12px] font-bold uppercase tracking-[0.14em] text-primary" data-testid="apply-eyebrow">
          {t`Apply to sell`}
        </span>

        <Typography variant="h1" className="mb-4">
          {t`Sell on Aldoo`}
        </Typography>

        <Typography 
          variant="body1_dim" 
          className="max-w-[720px] leading-relaxed mt-0 mb-10"
        >
          {t`Books, audiobooks, music and physical goods — Aldoo takes the payments, the delivery and the receipts, and your shop keeps its own orders, figures and payouts. Apply once; somebody reads every application.`}
        </Typography>
        
        <Registration
          org={null}
          fresh={anotherCompany}
          onChanged={(res) =>
            ListVendorOrgs().then((list) => {
              setOrgs(list?.orgs || [])
              // the new company is where its application now waits
              if (anotherCompany && res?.orgId) {
                setApplying("")
                setOrgId(res.orgId)
                setSection("registration")
              }
            })
          }
        />
      </div>
    )
  }

  const org = profile?.org
  // an admin of the company, or a platform admin looking in: the one who makes admins and composes roles
  const isOrgAdmin = profile?.myRole === "admin" || profile?.myRole === "platform-admin"
  // A company Aldoo has not approved may set itself up and nothing more:
  // selling, and everything that hangs off selling, waits for the decision.
  const verified = !org || org.verification !== "pending" && org.verification !== "rejected"
  // selling — orders, sales, products and the rest marked `contract` — opens once the payout
  // contract is signed; a company already selling before contracts were kept here (it has
  // products) keeps them meanwhile, and so does a platform admin looking in
  const selling = !org || !!profile?.payouts?.contractSigned || (profile?.productCount || 0) > 0 || profile?.myRole === "platform-admin"
  // a company not set up yet — its details missing, or no signed payout contract — has its Company and
  // Members sections only (and, while Aldoo decides, its registration): the rest opens once it is ready to
  // sell. A platform admin looking in sees every section, to help it get there
  const detailsMissing = eligibilityOf(org, { physical: !!profile?.hasPhysicalProducts, payouts: profile?.payouts, items: profile?.eligibility }).some((item) => !item.done && !item.optional && item.key !== "payoutContract")
  const setUp = !org || !profile || profile.myRole === "platform-admin" || (!!profile.payouts?.contractSigned && !detailsMissing)
  // Members is there for a company Aldoo has not decided on, not for one it turned down
  const early = (item) => item.early && org?.verification !== "rejected"
  const sections = SECTIONS.filter((item) => (verified ? !item.untilVerified : !item.verified && (!item.early || early(item))))
    .filter((item) => selling || !item.contract)
    .filter((item) => setUp || item.id === "company" || item.untilVerified || early(item))
    // and only the sections this member may see: none until the profile says what they may
    .filter((item) => can(item.permission))
  // a member who may read some section, shut out only because the company is not set up to sell yet
  const mayReadSome = SECTIONS.some((item) => can(item.permission))
  // a payout contract waiting for a signature this member may not give (an administrator, or whoever
  // holds payouts write, signs): the stop and the way to write to the administrators
  // the one who sets the company up (its details, contract, products): the guide and checklist are theirs
  const setsUp = can("vendor-company", "write") || can("vendor-payouts", "write") || can("vendor-products", "write")
  const contractBlocked = !!profile?.payouts?.contractPending && profile?.myRole !== "platform-admin" && !can("vendor-payouts", "write")
  // the section, and the sub-tab of it where an item is filled in (company=…, delivery=…), its field pointed at
  const openField = (section, target, tab) => {
    if (tab) writeUrl({ params: tab })
    setSection(section)
    highlightField(target)
  }
  // signing out leaves the vendor area for the site, rather than sitting on a
  // page that now belongs to nobody; asked first, as on the store's profile page
  const confirmSignOut = () =>
    showPromptDialog({
      title: t`Sign Out`,
      message: t`Are you sure you want to sign out?`,
      confirmText: t`Sign Out`,
      cancelText: t`Cancel`,
      variant: "admin",
      onConfirm: async () => {
        await logout()
        forgetVendorOrgs()
        rememberOrg(null, "")
        clientRedirect("/")
      },
    })
  // A company still waiting to hear opens on the page that has the news;
  // a verified one opens on its orders, which is what it came for.
  const home = verified ? sections[0]?.id : "registration"
  // a section the company cannot open yet (a link, the default Orders) lands on its first one
  const current = sections.some((item) => item.id === section) ? section : home
  // the sections that hold a missing detail of the company checklist (its contract item aside), and
  // Company while a contract waits to be signed
  // the guide still to go (any of its steps not done): it alone speaks, the checklist waits
  const guideSteps = profile?.org ? onboardingSteps({ org: profile.org, physical: !!profile.hasPhysicalProducts, payouts: profile.payouts, onboarding: profile.onboarding, productCount: profile.productCount, items: profile.eligibility }, {}) : []
  const guideDone = !guideSteps.length || guideSteps.every((step) => step.done)
  const lackingSections = new Set([
    ...eligibilityOf(profile?.org, { physical: !!profile?.hasPhysicalProducts, payouts: profile?.payouts, items: profile?.eligibility })
      .filter((item) => !item.done && !item.optional && item.key !== "payoutContract")
      .map((item) => item.section),
    ...(profile?.payouts?.contractPending ? ["company"] : []),
  ])

  // the sections as the menu shows them: open orders wait for someone (their count stays in sight from
  // every section); a section holding a missing detail (the checklist, a contract to sign) wears a red "!"
  const sectionTabs = sections.map(({ id, label, icon: Icon }) => ({
    value: id,
    label: t(label),
    icon: <Icon />,
    ...(id === "orders" && openOrders > 0
      ? { badge: openOrders > 99 ? "99+" : String(openOrders) }
      : lackingSections.has(id)
        ? { badge: "!", badgeClassName: "bg-admin_error" }
        : {}),
  }))

  // no organization chosen (or not yet known to be this person's): nothing of the
  // portal — no menu, no section — only the choice; closed without one: back to the
  // company it was opened from, else out to the site
  if (!allowed && !unknownOrg) {
    return (
      <div className="w-full mx-auto px-4 md:px-6 xl:px-8 py-6 md:py-10" data-testid="vendor-area">
        {!orgId ? <OrgPicker orgs={choices} onPick={(id) => { setChoosing(""); setOrgId(id) }} onNew={() => { setChoosing(""); setApplying("new") }} onClose={() => (switchingFrom ? setOrgId(switchingFrom) : clientRedirect("/"))} /> : <Empty>{t`Loading…`}</Empty>}
      </div>
    )
  }

  return (
    // the portal is a working screen: it takes the window's width (its tables and figures need it)
    <div className="w-full mx-auto px-4 md:px-6 xl:px-8 py-6 md:py-10 flex flex-col md:flex-row gap-6" data-testid="vendor-area">
      <PortalMenu tabs={sectionTabs} current={current} onSelect={openSection} onSignOut={confirmSignOut} onSwitch={choices.length > 1 ? switchOrg : null} org={org} />
      <ContractRequiredDialog open={contractStop} orgId={org?._id} onClose={() => setContractStop(false)} onOpenContract={() => openField("company", "contract-sign-form", { company: "contract" })} />
      {/* the organization is the subject of this page, so it names the panel */}
      {/* on a phone the ☰ menu holds the organization and its sections (PortalMenu): the page starts with the section */}
      <aside className="hidden md:flex md:w-[268px] shrink-0 md:flex-col gap-4" aria-label={t`Merchant Portal`}>
        <div className="md:sticky md:top-6 flex flex-col gap-4 w-full">
          <div className="flex items-center gap-3 px-1">
            <Thumb src={org?.logo ? orgLogoUrl(org._id, org.logoUpdatedAt) : null} name={org?.name || "?"} className="w-11 h-11 rounded-full text-base shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-[17px] font-bold truncate" data-testid="vendor-area-title">
                  {org?.name || "…"}
                </span>
                {org?.verification === "approved" && (
                  <CheckBadgeIcon className="w-5 h-5 shrink-0 text-admin_primary" title={t`Verified merchant`} data-testid="verified-badge" />
                )}
              </div>
              {/* on a phone the switch sits on the id's line, at the right; from 768px it has its own line below */}
              <div className="flex items-center gap-3 min-w-0">
                {org?.cid && <div className="text-xs text-admin_text_secondary truncate">{org.cid}</div>}
                {choices.length > 1 && (
                  <button
                    type="button"
                    onClick={switchOrg}
                    className="md:hidden ml-auto shrink-0 text-xs text-admin_primary hover:underline"
                    data-testid="vendor-switch-org-mobile"
                  >
                    {t`Switch organization`}
                  </button>
                )}
              </div>
            </div>
          </div>

          {choices.length > 1 && (
            <Button
              variant="admin_text"
              onClick={switchOrg}
              className="hidden md:block text-left px-1"
              data-testid="vendor-switch-org"
            >
              {t`Switch organization`}
            </Button>
          )}

          {/* from 768px the list down the side; on a phone the sections are the ☰ menu's (PortalMenu) */}
          <nav aria-label={t`Sections`} className="hidden md:block">
            <Tabs
              variant="sf_vendor"
              // the variant's own classes, handed over as well: a variant file
              // changed while the dev server runs may not be in its glob
              styleOverrides={tabsVariants.sf_vendor}
              value={current}
              onChange={setSection}
              tabs={sectionTabs}
            />
          </nav>

          <div className="hidden md:flex md:flex-col gap-1 border-t border-admin_bordered/70 pt-3">
            {/* the sellers' help center: the portal's screens explained, with the same "?" they carry */}
            <MenuItem icon={QuestionMarkCircleIcon} onClick={() => clientRedirect(sitePath("/vendor/help-center", language))} testId="vendor-help">
              {t`Merchant guide`}
            </MenuItem>
            <MenuItem icon={ArrowLeftOnRectangleIcon} onClick={confirmSignOut} testId="vendor-logout">
              {t`Sign Out`}
            </MenuItem>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col gap-4">
        {!profile && <Empty>{t`Loading…`}</Empty>}
        {profile?.error && <div className="text-admin_error">{profile.error}</div>}
        {/* a member given no role yet: nothing to open, and who to ask; one with a role, whose company
            is not ready to sell (its details or payout contract outstanding): what is waited for */}
        {org && !profile.error && contractBlocked && <ContractPendingNotice orgId={org._id} />}
        {org && !profile.error && !sections.length && !contractBlocked && (
          <div className="rounded-xl border border-admin_bordered bg-admin_paper px-5 py-8 text-center text-sm text-admin_text_secondary" data-testid={mayReadSome ? "vendor-not-set-up" : "vendor-no-access"}>
            {mayReadSome
              ? t("%s is not set up to sell yet: its sections open once an administrator of the company completes its details and signs the payout contract.", org.name)
              : t("You have no access to any section of %s yet. Ask an administrator of the company to give you a role.", org.name)}
          </div>
        )}
        {org && (
          <>
            {/* the products off the site: Aldoo's hold (and why), or the company's own switch — not on
                Settings, where the switch itself is */}
            {section !== "settings" && <CatalogHoldNotice org={org} onOpenSettings={() => setSection("settings")} />}
            {/* the way to selling, step by step, until every step is done: for the members who can take
                the steps (the company's details, its contract, its products), not for the rest */}
            {setsUp && <OnboardingGuide
              org={org}
              physical={!!profile.hasPhysicalProducts}
              payouts={profile.payouts}
              onboarding={profile.onboarding}
              productCount={profile.productCount}
              items={profile.eligibility}
              go={{
                fix: (item) => openField(item.section, item.target, item.tab),
                contract: () => openField("company", "contract-sign-form", { company: "contract" }),
                newProduct: () => {
                  setSection("products")
                  if (mayAddProducts) newProduct()
                },
                products: () => setSection("products"),
                // the product's dialog, opened on its Product Review tab
                reviewProduct: (pid) => writeUrl({ params: { section: "products", pid, ptab: "admin-review" } }),
                registration: () => setSection("registration"),
              }}
            />}
            {/* what the company still needs: one compact line on every page until it is complete — once
                the guide is done (until then the guide's "Company details" step says it, one thing at a
                time; both read the same list, profile.eligibility) */}
            {setsUp && guideDone && <EligibilityChecklist org={org} physical={!!profile.hasPhysicalProducts} payouts={profile.payouts} items={profile.eligibility} onFix={openField} />}
            {current === "registration" && <Registration org={org} onChanged={() => loadProfile({ quiet: true })} />}
            {current === "dashboard" && <StoreDashboards scope={org._id} headerClassName="bg-admin_paper" stickyTop="top-[60px]" advisor={ALDOO_ADVISOR} helpKeys={{ header: "vendor.dashboard#header", cards: "vendor.dashboard#cards" }} />}
            {current === "orders" && <OrdersTab orgId={org._id} viewer="vendor" />}
            {current === "sales" && <SalesTab orgId={org._id} timeZone={profile.reportingTimeZone} />}
            {current === "payouts" && <PayoutsPanel orgId={org._id} canEdit={can("vendor-payouts", "write")} onOpenDetails={() => setSection("company")} />}
            {current === "import" && <ImportTab orgId={org._id} canSchedule={can("vendor-import", "write")} />}
            {current === "feedback" && <FeedbackTable orgId={org._id} />}
            {current === "company" && (
              <>
                <ProfileEditor org={org} canEdit={can("vendor-company", "write")} productCount={profile.productCount} onSaved={orgSaved} allowDelete={false} />
                {SHOW_ANOTHER_COMPANY && <AnotherCompanyBanner t={t} onApply={() => setApplying("new")} />}
              </>
            )}
            {current === "settings" && <SettingsEditor org={org} canEdit={can("vendor-settings", "write")} onSaved={orgSaved} defaultTimeZone={profile.reportingTimeZone} isPlatformAdmin={profile.myRole === "platform-admin"} physical={!!profile.hasPhysicalProducts} />}
            {current === "payment-methods" && <PaymentMethodsEditor org={org} canEdit={can("vendor-payment-methods", "write")} admin={profile?.myRole === "platform-admin"} onSaved={orgSaved} />}
            {current === "shipping" && <ShippingEditor org={org} canEdit={can("vendor-shipping", "write")} onSaved={orgSaved} shippingCatalog={profile.shippingCatalog} physical={!!profile.hasPhysicalProducts} />}
            {current === "members" && <MembersPanel profile={profile} canManage={can("vendor-members", "write")} isOrgAdmin={isOrgAdmin} onChanged={() => loadProfile({ quiet: true })} />}
            {current === "products" && (
              <>
                {mayAddProducts ? (
                  <>
                    <div className="flex items-center justify-end gap-2">
                      {/* the help center's article on the list (vendor.products), and on adding a product */}
                      <HelpHint helpKey="vendor.products" iconClassName="w-[18px] h-[18px]" />
                      <Button 
                        variant="admin_primary" 
                        onClick={() => newProduct()} 
                        data-testid="vendor-new-product"
                      >
                        <PlusCircleIcon className="w-5 h-5" />
                        {t`New product`}
                      </Button>
                      <HelpHint helpKey="vendor.products#new" align="end" />
                    </div>
                    <SendForReview orgId={org._id} onBlocked={contractPending() ? () => setContractStop(true) : null} />
                  </>
                ) : (
                  // not before the contract: what to do instead, and the way to it
                  <div className="flex flex-wrap items-center gap-3 rounded-lg border border-admin_warning bg-admin_warning/5 px-4 py-3 text-sm text-admin_warning" data-testid="vendor-products-need-contract">
                    <span className="flex-1 min-w-[220px]">
                      {t`Sign the payout contract to add products and send them for review.`}
                    </span>
                    <Button 
                      variant="admin_primary" 
                      onClick={() => openField("company", "contract-sign-form", { company: "contract" })} 
                      data-testid="vendor-open-contract"
                    >
                      {t`Open the contract`}
                    </Button>
                  </div>
                )}
                {modelsReady && (
                  <StoreProducts
                    match={{ publisher: org._id }}
                    hideHeader
                    // the help center's words on each tab of the product dialog, beside its name (two articles)
                    helpKeyOf={productHelpKeyOf}
                    // a seller's product is written in Bulgarian first
                    defaultEditingLanguage="bg"
                    columnOverrides={vendorColumns}
                    thumbnailBadge={(item) => <ReviewDot status={item.publishStatus} />}
                    nameBadge={(item) => <AttentionChip item={item} attention={attention} />}
                    refreshSignal={productsVersion}
                    filters={vendorFilters.filters}
                    hiddenFilters={vendorFilters.hiddenFilters}
                    labels={productLabels}
                    noData={
                      <div className="flex flex-col items-center gap-3 py-6" data-testid="vendor-no-products">
                        <Typography variant="admin_txt1_dim">
                          {t`No products added yet`}
                        </Typography>
                        {mayAddProducts && (
                          <Button 
                            variant="admin_primary" 
                            onClick={() => newProduct()} 
                            data-testid="vendor-new-product-empty"
                          >
                            <PlusCircleIcon className="w-5 h-5" />
                            {t`New product`}
                          </Button>
                        )}
                      </div>
                    }
                  />
                )}
                {creatingProduct && (
                  <CreateProductDialog
                    orgId={org._id}
                    org={org}
                    onClose={() => setCreatingProduct(false)}
                    onChanged={() => setProductsVersion((version) => version + 1)}
                    onCreated={(newPid) => {
                      setCreatingProduct(false)
                      setPid(newPid)
                      // the first product: the guide's step follows
                      loadProfile({ quiet: true })
                    }}
                  />
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
